import { Audio } from 'expo-av';

const soundSources: Record<string, number> = {
  'flute-1-jazzy-puzzle-complete.mp3': require('../../assets/audio/flute-1-jazzy-puzzle-complete.mp3'),
  'flute-2-short-camera.mp3': require('../../assets/audio/flute-2-short-camera.mp3'),
  'flute-3-short-camera-1.mp3': require('../../assets/audio/flute-3-short-camera-1.mp3'),
  'flute-4-puzzle-complete.mp3': require('../../assets/audio/flute-4-puzzle-complete.mp3'),
  'flute-6-short.mp3': require('../../assets/audio/flute-6-short.mp3'),
  'flute-7-short.mp3': require('../../assets/audio/flute-7-short.mp3'),
  'flute-8-short.mp3': require('../../assets/audio/flute-8-short.mp3'),
  'flute-9-short.mp3': require('../../assets/audio/flute-9-short.mp3'),
  'flute-10-short.mp3': require('../../assets/audio/flute-10-short.mp3'),
  'flute-13-short-descending.mp3': require('../../assets/audio/flute-13-short-descending.mp3'),
  'flute-16-jazz-descending.mp3': require('../../assets/audio/flute-16-jazz-descending.mp3'),
  'clave-1.mp3': require('../../assets/audio/clave-1.mp3'),
};

class AudioManager {
  static isMuted = false;
  static lastPlayedFile: string | null = null;
  static introSounds = ['flute-16-jazz-descending.mp3'];
  static swatchSounds = ['flute-6-short.mp3', 'flute-7-short.mp3', 'flute-8-short.mp3', 'flute-9-short.mp3', 'flute-10-short.mp3'];
  static cameraSounds = ['flute-13-short-descending.mp3', 'flute-2-short-camera.mp3', 'flute-3-short-camera-1.mp3'];
  static completeSounds = ['flute-1-jazzy-puzzle-complete.mp3'];

  private static sounds: Record<string, Audio.Sound> = {};
  private static initPromise: Promise<void> | null = null;

  static setMuted(isMuted: boolean) {
    this.isMuted = isMuted;
  }

  static async init() {
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      await Audio.setAudioModeAsync({
        playsInSilentModeIOS: true,
        allowsRecordingIOS: false,
        staysActiveInBackground: false,
      });
      await this.preloadAll();
    })();

    return this.initPromise;
  }

  private static async preloadAll() {
    await Promise.all([
      this.preloadArray(this.introSounds),
      this.preloadArray(this.swatchSounds),
      this.preloadArray(this.cameraSounds),
      this.preloadArray(this.completeSounds),
    ]);
  }

  private static async preloadArray(arr: string[]) {
    await Promise.all(arr.map((file) => this.loadSound(file)));
  }

  private static async loadSound(file: string) {
    if (this.sounds[file]) return;
    const source = soundSources[file];
    if (!source) return;

    const { sound } = await Audio.Sound.createAsync(source, {
      shouldPlay: false,
      volume: 1,
    });

    this.sounds[file] = sound;
  }

  static async playRandom(arr: string[], volume = 0.5, delay = 0, maxDetuneDown = 0) {
    if (this.isMuted || arr.length === 0) return;

    await this.init();

    let file = arr[0];
    if (arr.length > 1) {
      do {
        file = arr[Math.floor(Math.random() * arr.length)];
      } while (file === this.lastPlayedFile);
    }

    this.lastPlayedFile = file;

    if (!this.sounds[file]) {
      await this.loadSound(file);
    }

    const sound = this.sounds[file];
    if (!sound) return;

    const detuneCents = maxDetuneDown > 0 ? Math.random() * maxDetuneDown : 0;
    const rate = Math.pow(2, -detuneCents / 1200);

    const play = async () => {
      try {
        await sound.setRateAsync(rate, true);
        await sound.setVolumeAsync(volume);
        await sound.replayAsync();
      } catch (error) {
        console.error('Error playing audio:', file, error);
      }
    };

    if (delay > 0) {
      setTimeout(() => {
        void play();
      }, delay * 1000);
    } else {
      await play();
    }
  }
}

export default AudioManager;
